<template lang="pug">
.course-tile(@click='routeToCourseInfoEditor', ref='el')
  .course-tile-wrapper
    .editor-elements-wrapper(@click.stop)
      .editor-elements
        .open-course-icon(@click.prevent.stop='routeToCourseInfoEditor')
          SVGRenderer(
            :has-hover='false',
            :icon='openCourseIcon',
            fill-color='var(--primary-text-color)',
            width='20px',
            height='20px'
          )
        h6.course-state(:class='course.state') {{ courseStateText(course.state) }}
      .course-group
        h6.groups-cta
          span(v-if='!visibleGroupsLength') ⚠️
          span {{ visibleForXGroups }}
        h6.change(@click.stop='showAssignCourseGroupModal') {{ $t('change') }}
    img.card-image(:src='course.cardTeaserImageUrl || cardEmptyImage')
    .tags-title
      h6.tags(v-if='isDesktopDevice || (isMobileDevice && courseTags)') {{ courseTags }}
      h2.course-tile-title(:style='{ height: computedHeight }', ref='courseTitleRef') {{ course.title }}
    .course-description-text
      HTMLRenderer.h5(:html='course.teaserDescription || ""', link-target='_blank')
    .meta-info
      .lessons-duration
        h6.lessons {{ $t('trainings.numberOfLessons', { number: course.numberOfLessons }) }}
        h6.duration {{ courseDuration }}h {{ $t('content') }}
      h6.ketch-font-bold {{ currencyFormatDE(productPrice(course)) }}
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

  import HTMLRenderer from '@/components/common/HTMLRenderer.vue'
  import useCourse from '@/composables/useCourse'
  import useCommonMixin from '@/composables/useCommonMixin'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'

  import { useRouter } from 'vue-router/composables'
  import useDuration from '@/composables/useDuration'
  import useIcons from '@/composables/useIcons'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useI18n from '@/composables/useI18n'

  import { CourseModule } from '@/store/modules/course'

  import type { PropType } from 'vue'
  import type { CourseItem } from '@/services/interfaces/Course'

  import eventBus from '@/main'

  const props = defineProps({
    course: {
      type: Object as PropType<CourseItem>,
      required: true,
    },
    maxHeight: {
      type: Number,
      default: 0,
    },
  })

  const emit = defineEmits(['course-data'])

  const router = useRouter()
  const { formatDurationToHHMM } = useDuration()
  const { guidedQuizDuration, courseGroupIds, productPrice, courseStateText } = useCourse()
  const { currencyFormatDE } = useCommonMixin()
  const { cardEmptyImage, openCourseIcon } = useIcons()
  const { isMobileDevice, isDesktopDevice, current } = useBreakpoint()
  const { translateString } = useI18n()

  const el = ref(null as HTMLDivElement | null)
  const courseTitleRef = ref(null as HTMLHeadingElement | null)

  const computedHeight = computed(() => (props.maxHeight ? props.maxHeight + 'px' : 'auto'))

  const courseDuration = computed(() => {
    const moduleDuration =
      CourseModule.coursesModuleMap[props.course.id]?.reduce((acc, mod) => {
        acc += guidedQuizDuration.value(mod.guidedQuiz?.numberOfQuestions || 0)
        return acc
      }, 0) || 0
    return formatDurationToHHMM((props.course.duration || 0) + moduleDuration)
  })

  const visibleGroupsLength = computed(() => courseGroupIds.value(props.course.id).length)

  const visibleForXGroups = computed(() =>
    translateString('trainings.visibleForXGroups', { groupsCount: visibleGroupsLength.value }),
  )

  const courseTags = props.course.tags.map((t) => t.title).join(', ')

  const routeToCourseInfoEditor = async () => {
    await CourseModule.setCourse(props.course)

    router.push({
      name: 'CourseInfoEditor',
      params: { courseId: props.course.id },
    })
  }

  const showAssignCourseGroupModal = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'AssignCourseGroupModal',
      modalProps: {
        course: props.course,
        groupIds: courseGroupIds.value(props.course.id),
      },
      cssClass: 'assign-course-group-modal',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const sendCourseData = () => {
    if (!courseTitleRef.value) return

    courseTitleRef.value.style.height = 'auto'

    nextTick(() => {
      const titleHeight = courseTitleRef.value?.offsetHeight || 0

      emit('course-data', {
        courseId: props.course.id,
        titleHeight,
        state: props.course.state,
      })

      courseTitleRef.value?.style.setProperty('height', computedHeight.value)
    })
  }

  let resizeTimeout: number | null = null
  const handleResize = () => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
    }
    resizeTimeout = window.setTimeout(() => {
      sendCourseData()
    }, 300)
  }

  watch(current, handleResize)

  onMounted(() => {
    setTimeout(() => {
      sendCourseData()
    }, 500)
  })

  onBeforeUnmount(() => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
    }

    emit('course-data', {
      courseId: props.course.id,
      titleHeight: 0,
      state: props.course.state,
    })
  })
</script>

<style lang="postcss">
  .course-tile {
    @apply ketch-transition-transform ketch-ease-in-out ketch-duration-500 hover:md:ketch-scale-110;
    @apply ketch-relative ketch-cursor-pointer ketch-transform-none;
    @apply ketch-h-full ketch-flex ketch-flex-col;

    .course-tile-wrapper {
      @apply ketch-relative ketch-overflow-hidden ketch-rounded-normal ketch-p-c10 ketch-bg-content-background-color;
      @apply ketch-w-full xl:ketch-w-c250 ketch-grid ketch-grid-cols-[120px_1fr] md:ketch-grid-cols-1;
      @apply ketch-h-full ketch-flex ketch-flex-col ketch-shadow-content-shadow;

      .editor-elements-wrapper {
        @apply ketch-flex ketch-flex-col ketch-space-y-c10 ketch-mb-c10 ketch-relative;

        .editor-elements,
        .course-group {
          @apply ketch-flex ketch-items-center ketch-justify-between;
        }

        .editor-elements {
          .course-state {
            &.in_progress,
            &.teaser_ready {
              @apply ketch-text-primary-color;
            }

            &.ready {
              @apply ketch-text-editor-primary-color;
            }
          }
        }

        .course-group {
          h6.groups-cta {
            @apply ketch-text-[11px];
          }

          h6 {
            @apply ketch-opacity-80;

            &.change {
              @apply hover:ketch-underline ketch-cursor-pointer ketch-lowercase;
            }
          }
        }
      }

      .card-image {
        @apply ketch-w-full ketch-object-cover ketch-rounded-[5px] ketch-row-start-1 ketch-row-end-4;
        @apply md:ketch-h-[205px] md:ketch-row-span-1 md:ketch-col-start-1 md:ketch-col-end-2 xl:ketch-h-[140px];
      }

      .tags-title {
        @apply ketch-row-span-1 ketch-col-start-2 ketch-col-end-3 md:ketch-row-span-2 md:ketch-col-start-1 md:ketch-col-end-2;
        @apply ketch-ml-c12 md:ketch-ml-0 ketch-inline-grid;

        .tags {
          @apply ketch-text-[#0D0D0D] ketch-text-opacity-80 ketch-my-c5 ketch-h-c16;
          @apply ketch-whitespace-nowrap ketch-overflow-hidden ketch-overflow-ellipsis;
        }

        .course-tile-title {
          @apply ketch-font-[600] ketch-whitespace-pre-wrap ketch-min-h-[22px] ketch-overflow-hidden;
          @apply ketch-text-sm md:ketch-text-md;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          line-clamp: 3;
          word-break: break-word;
        }
      }

      .course-description-text {
        @apply ketch-relative ketch-mt-c10 ketch-row-start-4 ketch-row-end-[8] ketch-col-start-1 ketch-col-end-3;
        @apply md:ketch-row-span-4 md:ketch-col-start-1 md:ketch-col-end-2;
        @apply ketch-flex-1;

        .content-container {
          @apply ketch-overflow-hidden;
          display: -webkit-box;

          p {
            @apply ketch-overflow-hidden ketch-mb-0 ketch-pb-0 xs4:ketch-h-[72px] md:ketch-h-c80 ketch-text-[13px];
            @apply ketch-text-primary-text-color ketch-text-opacity-70;
            display: -webkit-box;
            -webkit-line-clamp: 4;
            line-clamp: 4;
            -webkit-box-orient: vertical;
          }

          p ~ * {
            display: none;
          }
        }
      }

      .meta-info {
        @apply ketch-flex ketch-items-center ketch-justify-between ketch-mt-c10 ketch-row-start-[9];
        @apply ketch-col-start-1 ketch-col-end-3 md:ketch-row-span-5 md:ketch-col-start-1 md:ketch-col-end-2;
        @apply ketch-mt-auto;

        .lessons-duration {
          @apply ketch-flex ketch-items-center ketch-space-x-c15;

          h6 {
            @apply ketch-text-[#C9C9C9];
          }
        }
      }
    }
  }
</style>
